import {
  INVOICE_BUYER_IDENTITY_THRESHOLD_MINOR,
  TAX_INVOICE_THRESHOLD_MINOR,
} from "@stb/jurisdiction-au"
import { PRODUCT_CONFIG } from "@stb/product-config"
import {
  Banknote,
  Briefcase,
  Calculator,
  FileText,
  Mail,
  Printer,
  Users,
  Wrench,
  XCircle,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { InvoiceIllustration } from "../../../components/home/home-art"
import { InvoiceHeroMock, ListMock } from "../../../components/landing/product-art"
import { type ProductPageContent, withIcons } from "../../../components/landing/product-page"
import { aud, INVOICE_EXAMPLE as ex } from "../../products/example-numbers"
import { ATO_TAX_INVOICES, INVOICING } from "../../products/invoicing"
import { englishDateToZh } from "../dates"

/** Chinese counterpart of products/invoicing.tsx; terms: docs/design/landing/copy-zh.md. */
const wholeAud = (cents: number) => aud(cents, true)
const buyerThreshold = wholeAud(INVOICE_BUYER_IDENTITY_THRESHOLD_MINOR)
const receiptThreshold = wholeAud(TAX_INVOICE_THRESHOLD_MINOR)

const CHECKED_ZH = englishDateToZh(ATO_TAX_INVOICES.checked)

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const ato = (
  <a href={ATO_TAX_INVOICES.href} className={linkClass}>
    ATO，Tax Invoice
  </a>
)

export const INVOICING_SEO_ZH = {
  title: "Tax Invoice：创建、发送、收款",
  description: `按 ATO 列出的要求开出 Tax Invoice，用邮件发送 PDF，并跟踪谁已付款。免费版每月可开 ${PRODUCT_CONFIG.plans.free.invoicesMonthly} 张发票。`,
}

const COPY: ProductPageContent = {
  path: "/invoicing",
  name: "开发票",
  tag: "Tax Invoice",
  reviewed: englishDateToZh(INVOICING.reviewed),
  h1: "个体经营者几分钟就能发出 Tax Invoice",
  lead: "选好客户、加上项目，就能发出写明 ATO 所列信息的 PDF Tax Invoice。谁已付款、谁已逾期，一目了然。",
  answer: {
    text: `金额达到 ${buyerThreshold} 或以上的销售，Tax Invoice 必须写明买方身份或 ABN，并说明总额含 GST 或列出 GST 金额。如果你没有登记 GST，就开不含 GST 的普通发票。`,
    sources: [{ label: "ATO，Tax Invoice", href: ATO_TAX_INVOICES.href }],
    checked: CHECKED_ZH,
  },
  cta: { label: "开始免费试用", href: "/login" },
  secondaryCta: { label: "查看价格", href: "/pricing" },
  heroNote: `免费版每月可开 ${PRODUCT_CONFIG.plans.free.invoicesMonthly} 张发票。`,
  stats: [
    { value: "10%", label: "GST 自动算好" },
    { value: `每月 ${PRODUCT_CONFIG.plans.free.invoicesMonthly} 张`, label: "免费版发票数量" },
    { value: "PDF", label: "邮件发送，并与发票一起保存" },
    { value: "部分付款", label: "付款记录与逾期状态" },
  ],
  stepsTitle: "创建、发送、收款",
  steps: [
    { title: "创建", body: "选择客户并添加项目，GST 自动算好。" },
    {
      title: "发送",
      body: "用邮件把 PDF 发给客户。发票号在发送时确定，发送后有几秒钟可以撤销。",
    },
    { title: "跟踪付款", body: "记录全额或部分付款。超过到期日的发票会显示为逾期。" },
  ],
  example: {
    title: "发票示例",
    caption: "含 GST 的发票示例",
    columns: ["项目", "金额"],
    rows: [
      ...ex.lines.map((l) => ({ header: l.description, cells: [l.amount] })),
      { header: "小计", cells: [ex.subtotal] },
      { header: "GST（10%）", cells: [ex.gst] },
      { header: "合计", cells: [ex.total] },
    ],
    note: `仅为示例：开给 ${ex.customer} 的发票。金额为加上 10% GST 之前的不含 GST 金额。`,
  },
  included: [
    { title: "客户", body: "姓名、邮箱和可选的 ABN，放在同一份清单里。" },
    {
      title: "普通发票或 Tax Invoice",
      body: "已登记 GST：开 Tax Invoice。未登记：开不显示 GST 的普通发票。",
    },
    {
      title: "GST 自动计算",
      body: "每行或整张发票按 10% 计算，舍入方式由你选。属于 GST-free 的行可以单独标记。",
    },
    { title: "编号、PDF 和邮件", body: "自动编号，PDF 与预览一致，发送失败时可以重试。" },
    { title: "付款", body: "记录全额或部分付款，或直接标记已付。逾期的发票会被标出。" },
    { title: "打印", body: "发票发送后，可以下载 PDF 并在你的设备上打印。" },
  ],
  limits: {
    title: "不含付款收据、刷卡收款和 BAS",
    body: (
      <>
        我们不开付款收据，不替你刷卡收款，也不代你申报
        BAS。记录一笔付款只是给发票做标记，不会转移资金。Kestly
        提供的是估算和一般性信息，不是税务建议。来源：{ato}，核对于 {CHECKED_ZH}。
      </>
    ),
  },
  who: [
    { title: "水电工和上门服务", body: "活干完当天，就在办公桌前把发票发出去。" },
    { title: "承包商和顾问", body: "回头客、清晰的编号、简单的催款。" },
    { title: "有 ABN 的网约车和外卖司机", body: "给自己的客户开发票，记录集中放在一处。" },
  ],
  plan: {
    rows: ["invoices"],
    note: "Logbook 套餐不含开发票。",
  },
  faqTitle: "开发票常见问题",
  faq: [
    {
      question: "Tax Invoice 必须写明什么？",
      answer: (
        <>
          “Tax Invoice”字样、你的名称和
          ABN、日期、所售内容的说明及数量和价格、每笔销售中应税的部分，以及 GST 金额或总额含 GST
          的说明。达到 {buyerThreshold} 起，还要写明买方的名称或 ABN。来源：{ato}
          ，核对于 {CHECKED_ZH}。
        </>
      ),
    },
    {
      question: "小额销售也要开 Tax Invoice 吗？",
      answer: (
        <>
          客户要求时，你必须在 28 天内提供 Tax Invoice，除非这笔销售含 GST 在内不超过{" "}
          {receiptThreshold}。来源：{ato}，核对于 {CHECKED_ZH}。
        </>
      ),
    },
    {
      question: "可以打印发票吗？",
      answer: "可以，发票发送之后即可。下载 PDF 后打印，或另存为文件。",
    },
    {
      question: "你们会开付款收据吗？",
      answer: "不会。你可以在发票上记录付款。",
    },
  ],
  related: [
    { label: "Tax Invoice 要求", href: "/guides/tax-invoice-requirements" },
    { label: "免费发票生成器", href: "/invoice-generator" },
    { label: "收据和开支", href: "/receipt-scanner" },
    { label: "使用方法", href: "/how-it-works" },
    { label: "价格", href: "/pricing" },
    { label: "里程记录", href: "/mileage-tracker" },
  ],
  band: {
    title: "发出你的第一张 Tax Invoice",
    description: `免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`,
  },
}

const STEP_ART = [
  <InvoiceIllustration
    key="create"
    invoice={{
      no: "INV-0044",
      to: ex.customer,
      lines: ex.lines.map((l) => [l.description, l.amount]),
      subtotal: ex.subtotal,
      gst: ex.gst,
      total: ex.total,
    }}
  />,
  <ListMock
    key="send"
    rows={[
      {
        title: `已发送给 ${ex.customer}`,
        sub: "附 PDF · 10月8日 周四",
        badge: "已发送",
        tone: "success",
      },
    ]}
  />,
  <ListMock
    key="track"
    rows={[
      {
        title: `INV-0044 · ${ex.customer}`,
        sub: "10月22日 周四到期",
        badge: "已发送",
        tone: "primary",
      },
      {
        title: "INV-0038 · Westside Cafe",
        sub: "10月6日 周二到期",
        badge: "已逾期",
        tone: "destructive",
      },
    ]}
  />,
]

/** Icons and pictures are presentation only (same as the English page). */
export const INVOICING_ZH: ProductPageContent = {
  ...COPY,
  heroCard: <InvoiceHeroMock />,
  stats: withIcons(COPY.stats, [Calculator, FileText, Mail, Banknote]),
  steps: COPY.steps.map((step, i) => ({ ...step, art: STEP_ART[i], artOnMobile: i > 0 })),
  included: withIcons(COPY.included, [Users, FileText, Calculator, Mail, Banknote, Printer]),
  limits: { ...COPY.limits, icon: XCircle },
  who: withIcons(COPY.who, [Wrench, Briefcase, Users]),
}
